import { Html5Qrcode } from 'html5-qrcode';
import { checkInLog, startTimedCheckInLog } from './checkInClientLogger';

export type CheckInCameraEngineKind = 'barcode-detector' | 'html5-qrcode';

export interface CheckInCameraEngine {
  kind: CheckInCameraEngineKind;
  pause: () => void;
  resume: () => void;
  stop: () => Promise<void>;
  getDecodeCount: () => number;
}

type BarcodeDetectorLike = {
  detect: (source: ImageBitmapSource) => Promise<Array<{ rawValue?: string }>>;
};

type BarcodeDetectorCtor = new (options?: { formats?: string[] }) => BarcodeDetectorLike;

function getBarcodeDetectorCtor(): BarcodeDetectorCtor | null {
  if (typeof window === 'undefined') return null;
  const ctor = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
  return typeof ctor === 'function' ? ctor : null;
}

export async function isBarcodeDetectorSupported(): Promise<boolean> {
  const Ctor = getBarcodeDetectorCtor();
  if (!Ctor) return false;
  try {
    const formatsFn = (Ctor as unknown as { getSupportedFormats?: () => Promise<string[]> })
      .getSupportedFormats;
    if (typeof formatsFn === 'function') {
      const formats = await formatsFn.call(Ctor);
      return formats.map((f) => f.toLowerCase()).includes('qr_code');
    }
    return true;
  } catch {
    return false;
  }
}

function errorToText(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`.trim();
  return String(err || '');
}

async function waitForVisibleReader(elementId: string, timeoutMs = 4000): Promise<HTMLElement> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const element = document.getElementById(elementId);
    if (element) {
      const { width, height } = element.getBoundingClientRect();
      if (width > 0 && height > 0) return element;
    }
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
  }
  throw new Error(`Elemento da câmera não ficou visível (#${elementId})`);
}

export function clearReaderElement(element: HTMLElement): void {
  element.replaceChildren();
}

const HTML5_SCANNER_CONFIG = {
  fps: 16,
  aspectRatio: 1.0,
  disableFlip: false,
  qrbox: (viewfinderWidth: number, viewfinderHeight: number) => {
    const side = Math.max(
      200,
      Math.min(
        Math.floor(Math.min(viewfinderWidth, viewfinderHeight) * 0.92),
        Math.floor(Math.max(viewfinderWidth, viewfinderHeight) * 0.88)
      )
    );
    return { width: side, height: side };
  },
};

async function listCameraConfigs(): Promise<Array<string | { facingMode: string }>> {
  const configs: Array<string | { facingMode: string }> = [
    { facingMode: 'environment' },
    { facingMode: 'user' },
  ];
  try {
    const cameras = await Html5Qrcode.getCameras();
    for (const cam of cameras || []) {
      if (cam.id) configs.push(cam.id);
    }
  } catch {
    /* getCameras pode falhar antes da permissão */
  }
  return configs;
}

async function startHtml5Engine(
  elementId: string,
  onDecode: (text: string) => void
): Promise<CheckInCameraEngine> {
  const readerElement = await waitForVisibleReader(elementId);
  const configs = await listCameraConfigs();
  const seen = new Set<string>();
  const uniqueConfigs = configs.filter((config) => {
    const key = typeof config === 'string' ? config : config.facingMode;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  checkInLog('info', 'camera_configs', `Tentando ${uniqueConfigs.length} config(s) html5-qrcode`, {
    configs: uniqueConfigs.map((c) => (typeof c === 'string' ? c.slice(0, 24) : c.facingMode)),
  });

  let lastError: unknown = null;
  let decodeCount = 0;

  for (const cameraConfig of uniqueConfigs) {
    const key = typeof cameraConfig === 'string' ? cameraConfig.slice(0, 24) : cameraConfig.facingMode;
    const timed = startTimedCheckInLog('camera_start_try', `Iniciando html5: ${key}`, {
      camera: key,
      engine: 'html5-qrcode',
    });
    clearReaderElement(readerElement);
    const scanner = new Html5Qrcode(elementId);
    try {
      await scanner.start(cameraConfig, HTML5_SCANNER_CONFIG, (decoded) => {
        decodeCount += 1;
        onDecode(decoded);
      }, () => undefined);
      timed.end('info', `Câmera OK (html5): ${key}`);
      // pause/resume existem em runtime, mas nem sempre no tipado da lib
      const controllable = scanner as Html5Qrcode & {
        pause?: (keepVideo?: boolean) => void;
        resume?: () => void;
      };
      return {
        kind: 'html5-qrcode',
        getDecodeCount: () => decodeCount,
        pause: () => {
          try {
            controllable.pause?.(true);
          } catch {
            /* ignore */
          }
        },
        resume: () => {
          try {
            controllable.resume?.();
          } catch {
            /* ignore */
          }
        },
        stop: async () => {
          try {
            await scanner.stop();
            await scanner.clear();
          } catch {
            /* ignore */
          }
        },
      };
    } catch (err) {
      lastError = err;
      timed.end('warn', `Falha html5: ${key}`, { error: errorToText(err) });
      try {
        await scanner.stop();
      } catch {
        /* ignore */
      }
      try {
        await scanner.clear();
      } catch {
        /* ignore */
      }
      clearReaderElement(readerElement);
    }
  }

  throw lastError || new Error('Nenhuma câmera disponível (html5-qrcode)');
}

const VIDEO_CONSTRAINT_SETS: MediaStreamConstraints[] = [
  {
    video: {
      facingMode: { ideal: 'environment' },
      width: { ideal: 1280 },
      height: { ideal: 720 },
    },
    audio: false,
  },
  {
    video: {
      facingMode: { ideal: 'environment' },
    },
    audio: false,
  },
  {
    video: { facingMode: 'user' },
    audio: false,
  },
  { video: true, audio: false },
];

async function openVideoStream(): Promise<MediaStream> {
  let lastError: unknown = null;
  for (const constraints of VIDEO_CONSTRAINT_SETS) {
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError || new Error('Não foi possível abrir a câmera');
}

async function startBarcodeDetectorEngine(
  elementId: string,
  onDecode: (text: string) => void
): Promise<CheckInCameraEngine> {
  const Ctor = getBarcodeDetectorCtor();
  if (!Ctor) throw new Error('BarcodeDetector indisponível');

  const readerElement = await waitForVisibleReader(elementId);
  clearReaderElement(readerElement);

  const timed = startTimedCheckInLog('camera_start_try', 'Iniciando BarcodeDetector', {
    engine: 'barcode-detector',
  });

  const detector = new Ctor({ formats: ['qr_code'] });
  const stream = await openVideoStream();
  const video = document.createElement('video');
  video.setAttribute('playsinline', 'true');
  video.setAttribute('muted', 'true');
  video.muted = true;
  video.autoplay = true;
  video.style.width = '100%';
  video.style.height = '100%';
  video.style.objectFit = 'cover';
  video.style.borderRadius = '8px';
  video.srcObject = stream;
  readerElement.appendChild(video);

  try {
    await video.play();
  } catch (err) {
    stream.getTracks().forEach((t) => t.stop());
    timed.end('warn', 'Falha ao dar play no vídeo', { error: errorToText(err) });
    throw err;
  }

  let decodeCount = 0;
  let paused = false;
  let stopped = false;
  let rafId = 0;
  let lastDetectAt = 0;
  let lastEmitAt = 0;
  let lastEmitted = '';
  const DETECT_EVERY_MS = 90;
  const EMIT_COOLDOWN_MS = 900;

  const tick = async (now: number) => {
    if (stopped) return;
    rafId = requestAnimationFrame(() => {
      void tick(performance.now());
    });
    if (paused) return;
    if (now - lastDetectAt < DETECT_EVERY_MS) return;
    if (video.readyState < 2) return;
    lastDetectAt = now;
    try {
      const codes = await detector.detect(video);
      if (stopped || paused) return;
      const raw = codes.find((c) => c.rawValue)?.rawValue?.trim();
      if (!raw) return;
      // Evita spam do mesmo QR a cada frame antes do pause da API
      if (raw === lastEmitted && now - lastEmitAt < EMIT_COOLDOWN_MS) return;
      lastEmitted = raw;
      lastEmitAt = now;
      decodeCount += 1;
      onDecode(raw);
    } catch {
      /* frame falhou — segue o loop */
    }
  };

  rafId = requestAnimationFrame(() => {
    void tick(performance.now());
  });
  timed.end('info', 'Câmera OK (BarcodeDetector)');

  return {
    kind: 'barcode-detector',
    getDecodeCount: () => decodeCount,
    pause: () => {
      paused = true;
    },
    resume: () => {
      paused = false;
    },
    stop: async () => {
      stopped = true;
      if (rafId) cancelAnimationFrame(rafId);
      stream.getTracks().forEach((t) => t.stop());
      video.srcObject = null;
      video.remove();
      clearReaderElement(readerElement);
    },
  };
}

/** Abre câmera: BarcodeDetector nativo primeiro; html5-qrcode como fallback. */
export async function startCheckInCameraEngine(
  elementId: string,
  onDecode: (text: string) => void,
  onDecodeMeta?: (info: { count: number; engine: CheckInCameraEngineKind; codeLen: number }) => void
): Promise<CheckInCameraEngine> {
  const nativeOk = await isBarcodeDetectorSupported();
  checkInLog('info', 'camera_engine_pick', nativeOk ? 'Preferindo BarcodeDetector' : 'Só html5-qrcode', {
    barcodeDetector: nativeOk,
  });

  const bind =
    (engineKind: CheckInCameraEngineKind, getCount: () => number) =>
    (decoded: string) => {
      const count = getCount();
      onDecodeMeta?.({ count, engine: engineKind, codeLen: decoded?.length || 0 });
      onDecode(decoded);
    };

  if (nativeOk) {
    try {
      // Contador compartilhado: o engine incrementa antes de chamar onDecode.
      let engineRef: CheckInCameraEngine | null = null;
      engineRef = await startBarcodeDetectorEngine(elementId, (decoded) => {
        bind('barcode-detector', () => engineRef?.getDecodeCount() ?? 0)(decoded);
      });
      return engineRef;
    } catch (err) {
      checkInLog('warn', 'camera_engine_fallback', 'BarcodeDetector falhou; tentando html5-qrcode', {
        error: errorToText(err),
      });
    }
  }

  let engineRef: CheckInCameraEngine | null = null;
  engineRef = await startHtml5Engine(elementId, (decoded) => {
    bind('html5-qrcode', () => engineRef?.getDecodeCount() ?? 0)(decoded);
  });
  return engineRef;
}

export async function ensureCameraPermission(): Promise<void> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('getUserMedia não suportado neste navegador');
  }
  const constraintSets: MediaStreamConstraints[] = [
    { video: { facingMode: { ideal: 'environment' } }, audio: false },
    { video: { facingMode: 'user' }, audio: false },
    { video: true, audio: false },
  ];
  let lastError: unknown = null;
  for (const constraints of constraintSets) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      stream.getTracks().forEach((track) => track.stop());
      await new Promise<void>((resolve) => setTimeout(resolve, 150));
      return;
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError || new Error('Permissão da câmera negada');
}

export { waitForVisibleReader };
